'use client';

// Opted out of React Compiler, as the grid it wraps is. Rows, columns and headers keep their
// identity when pinning, order or visibility change, so compiled reads such as
// `row.getStartVisibleCells()` were cached against the unchanged row and showed stale cells once
// a compiled consumer passed a stable render prop (`grid-table-sync.browser.test.tsx`).
'use no memo';

// Today's composable table API (`TableProvider`, `TableHeader`, `TableHeaderGroup`, `TableHead`,
// `TableBody`, `TableRow`, `TableCell`, `TableFooter`) rebuilt from the ReUI data grid's building
// blocks. The table comes from `TableStateProvider`; the grid never creates or reconfigures it.
import { createContext, Fragment, type ReactNode, type RefObject, useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_PAGE_SIZE, TEST_IDS } from '@repo/sdk/constants';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Skeleton } from '@repo/ui/components';
import { useIsMobile } from '@repo/ui/hooks';
import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon, XIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { TruncatedText } from '../cells/truncated-text';
import { DataGrid, type DataGridProps, type DataGridRowProps, getColumnHeaderLabel, useDataGrid } from '../data-grid/data-grid';
import { DataGridTableBase, DataGridTableBody, DataGridTableBodyRow, DataGridTableBodyRowCell, DataGridTableBodyRowSkeleton, DataGridTableBodyRowSkeletonCell, DataGridTableFillBodyCell, DataGridTableFillFootCell, DataGridTableFillHeadCell, DataGridTableFoot, DataGridTableFootRow, DataGridTableFootRowCell, DataGridTableHead, DataGridTableHeadRow, DataGridTableHeadRowCell, DataGridTableHeadRowCellResize, DataGridTableViewport, renderDataGridTableHeadCellContent } from '../data-grid/data-grid-table';
import { DataGridTableVirtual } from '../data-grid/data-grid-table-virtual';
import { HeaderDragGrip, HeaderDragProvider, useHeaderDragCell } from './header-drag';
import type { TableLinkComponent, TableLinkProps, TableProviderProps } from './table-types';
import { focusSelectionCheckbox, isSelectionColumn } from '../table/components/selection-column';
import { TableBulkActionBar } from '../table/components/table-bulk-action-bar';
import { RowSelectionGesturesContext, useRowSelectionGestures } from '../table/hooks/use-row-selection-gestures';
import { CellColumnIdContext } from '../table/context/column-settings';
import { useTableState } from '../table/context/table-state-provider';
import { hasOneLineHeader, useRowDensity } from '../table/row-density';
import { getStickySides } from './sticky-columns';
import { flexRender } from '../table/tanstack-table';
import { Subscribe } from '@tanstack/react-table';
import type { Cell, Column, Header, Row, RowData, TableFeatures } from '../table/tanstack-table';
export type { TableLinkComponent, TableLinkProps, TableProviderProps } from './table-types';

// Two contexts, each holding a prop as is, so a provider render doesn't re-render every cell.
const TableLinkComponentContext = createContext<null | TableLinkComponent>(null);
const TableLinkContext = createContext<unknown>(undefined);
const selectLayout = (state: {
  columnOrder: string[];
  columnPinning: {
    end: string[];
    start: string[];
  };
  columnVisibility: Record<string, boolean>;
}) => ({
  columnOrder: state.columnOrder,
  columnPinning: state.columnPinning,
  columnVisibility: state.columnVisibility
});
const selectProviderLayout = (state: {
  columnOrder: string[];
  columnPinning: {
    end: string[];
    start: string[];
  };
  columnSizing: Record<string, number>;
  columnVisibility: Record<string, boolean>;
}) => ({
  ...selectLayout(state),
  columnSizing: state.columnSizing
});

// The look of the `@repo/table` renderer (`@repo/ui`'s table parts), carried over to the grid.
// Rows are opaque and pinned cells inherit their row's colour, so the stripe, hover and selection
// run under pinned columns too. A pinned cell must stay opaque to hide what scrolls under it.
const ROW_CLASS = cn('bg-background transition-colors hover:bg-muted data-[state=selected]:bg-table-selected! data-[state=selected]:hover:bg-table-selected!', 'hover:[&>td[data-pinned]]:bg-inherit data-[state=selected]:[&>td[data-pinned]]:bg-inherit',
// The fill cell, when no column takes the free width, continues the row's line.
'[&>td[data-slot=data-grid-table-fill-body-cell]]:border-b',
// A linked cell's hit area fills its cell.
'[&>td:has([data-slot=table-cell-link-area])]:relative');
const STRIPED_ROW_CLASS = 'even:bg-table-striped even:hover:bg-table-striped-hover';
const CELL_CLASS = cn('border-b border-border p-2 [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-0.5', 'overflow-visible text-clip whitespace-normal data-pinned:bg-inherit data-pinned:shadow-none!',
// The grid clips a pinned cell's background inside its border, which shows through the
// translucent dark-mode border.
'data-pinned:bg-clip-border!');
// White and sticky, with a hairline under it. The end padding is the content's
// (`HEADER_CONTENT_CLASS`). Each head shows its own drag grip on hover.
const HEAD_CELL_CLASS = cn('group/head h-auto bg-card px-2 py-2.5 pe-0 text-wrap data-pinned:bg-card *:[[role=checkbox]]:translate-y-0.5', 'shadow-[inset_0_-1px_0_var(--border)]!');
const HEADER_ROW_CLASS = cn('group/header', '[&>th[data-slot=data-grid-table-fill-head-cell]]:bg-card [&>th[data-slot=data-grid-table-fill-head-cell]]:shadow-[inset_0_-1px_0_var(--border)]');
// The grabbing cursor over the whole table while a header drags.
const TABLE_DRAGGING_CLASS = 'data-header-drag:cursor-grabbing data-header-drag:**:cursor-grabbing!';
// A short bar inside the column's end edge. An end-pinned column keeps its handle at the start
// edge, which it resizes from.
const RESIZE_HANDLE_CLASS = cn('end-0 w-4 before:absolute before:inset-y-auto before:end-px before:top-1/2 before:block before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-border before:opacity-60 before:transition-all before:duration-150', 'hover:before:h-6 hover:before:w-1 hover:before:bg-primary/80 hover:before:opacity-100', 'data-resizing:before:h-6 data-resizing:before:w-1 data-resizing:before:bg-primary data-resizing:before:opacity-100', 'in-data-[pinned=end]:end-auto in-data-[pinned=end]:before:start-px in-data-[pinned=end]:before:end-auto');

/** One head row, with its headers split by pinning as the grid renders them. */
export type TableHeaderGroupModel = {
  centerHeaders: Header<RowData, unknown>[];
  endHeaders: Header<RowData, unknown>[];
  id: string;
  /** The number of head rows, which the fill cell spans. */
  rowCount: number;
  rowIndex: number;
  startHeaders: Header<RowData, unknown>[];
};
export type TableHeaderProps = {
  children: (props: {
    headerGroup: TableHeaderGroupModel;
  }) => ReactNode;
};
type GridRootProps<TData extends RowData> = Omit<TableProviderProps<TData>, 'children' | 'style'> & {
  /** Classes for every row's body cells, after the grid's own; skeleton cells keep the grid's. */
  cellClassName?: string;
  /** The grid: a table in its viewport. */
  children: ReactNode;
  getRowProps?: (row: TData) => DataGridRowProps | undefined;
  /** The grid's own viewport, whose parent scrolls when there's no `viewportRef`. */
  gridViewportRef?: RefObject<HTMLDivElement | null>;
};
export function TableProvider<TData extends RowData>({
  children,
  style,
  ...props
}: TableProviderProps<TData>) {
  const {
    tableRef
  } = useTableState(() => ({}));
  const gridViewportRef = useRef<HTMLDivElement | null>(null);
  return <GridRoot {...props} gridViewportRef={gridViewportRef} data-sentry-element="GridRoot" data-sentry-component="TableProvider" data-sentry-source-file="table.tsx">
      <DataGridTableViewport style={style} viewportRef={gridViewportRef} data-sentry-element="DataGridTableViewport" data-sentry-source-file="table.tsx">
        <DataGridTableBase tableRef={tableRef} data-sentry-element="DataGridTableBase" data-sentry-source-file="table.tsx">{children}</DataGridTableBase>
      </DataGridTableViewport>
    </GridRoot>;
}

/**
 * The grid with what every table shares around it: the data hand-off, the look, cell links,
 * sticky sides, and the bulk action bar with its selection focus.
 */
function GridRoot<TData extends RowData>({
  bulkActionsOverlay,
  cellClassName,
  children,
  className,
  data,
  externalViewport = false,
  getRowProps,
  gridViewportRef,
  isStriped = true,
  linkComponent,
  linkContext,
  viewportRef
}: GridRootProps<TData>) {
  // Row-agnostic, as the grid's header and cell renderers are; `data` still types the props.
  // Only the layout it reads, so sorting or row selection doesn't re-render the whole grid.
  const {
    bulkActions,
    columnPinning,
    columnSizing,
    setData,
    table
  } = useTableState(selectProviderLayout);
  const selectionFocusIdRef = useRef<null | string>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isHeaderDraggingRef = useRef(false);
  const [viewportWidth, setViewportWidth] = useState<null | number>(null);
  const isMobile = useIsMobile();
  const isOneLineHeader = hasOneLineHeader(useRowDensity());
  // A live resize drag's widths, which the grid commits only on release. Phones only, as only
  // the phone policy reads them; elsewhere a drag stays a CSS-variable update.
  const [sizingPreview, setSizingPreview] = useState<null | Record<string, number>>(null);
  // Pinned columns always stick. Only on phones do the ones past half the width scroll, so the
  // table stays readable, re-checked as a drag moves. A plain object, so the grid compares it by
  // value.
  const stickySides = Object.fromEntries(getStickySides(table, columnPinning, {
    ...columnSizing,
    ...sizingPreview
  }, isMobile ? viewportWidth : null));

  // The grid's own viewport is as wide as the table, so the box that scrolls is its parent.
  const getViewport = () => viewportRef?.current ?? (externalViewport ? null : gridViewportRef?.current?.parentElement ?? null);
  const rowSelectionGestures = useRowSelectionGestures({
    enabled: Boolean(bulkActions),
    getViewport,
    table
  });
  useEffect(() => {
    const viewport = getViewport();
    if (!viewport) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setViewportWidth(entry.contentRect.width);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [externalViewport, gridViewportRef, viewportRef]);

  // Drops selected rows the new data no longer holds. An effect event, because `table` is a new
  // view on every render and would re-run the effect below each time.
  const pruneSelection = useEffectEvent((rows: TData[]) => {
    if (!bulkActions) return;
    const currentRowIds = new Set(rows.map((row, index) => bulkActions.getRowId(row, index)));
    table.setRowSelection(currentSelection => {
      const selectedIds = Object.keys(currentSelection);
      if (selectedIds.every(id => currentRowIds.has(id))) return currentSelection;
      const nextSelection: Record<string, true> = {};
      for (const id_0 of selectedIds) {
        if (currentRowIds.has(id_0)) nextSelection[id_0] = true;
      }
      return nextSelection;
    });
  });
  useEffect(() => {
    setData(data);
    pruneSelection(data);
  }, [data, setData]);
  const restoreSelectionFocus = () => {
    requestAnimationFrame(() => {
      const selectionFocusId = selectionFocusIdRef.current;
      const selectionCheckbox = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[data-table-selection-checkbox]') ?? []).find(checkbox => checkbox.dataset.tableSelectionCheckbox === selectionFocusId);
      if (selectionCheckbox && !selectionCheckbox.matches(':disabled')) {
        selectionCheckbox.focus();
        return;
      }
      focusSelectionCheckbox(containerRef.current ?? undefined);
    });
  };
  const handleSelectionFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    const selectionCheckbox_0 = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-table-selection-checkbox]') : null;
    if (selectionCheckbox_0) {
      selectionFocusIdRef.current = selectionCheckbox_0.dataset.tableSelectionCheckbox ?? null;
    }
  };
  const handleTableKeyDown = (event_0: React.KeyboardEvent<HTMLDivElement>) => {
    // A header drag takes Escape first, to cancel itself.
    if (event_0.key !== 'Escape' || isHeaderDraggingRef.current || !bulkActions || table.getSelectedRowModel().rows.length === 0) {
      return;
    }
    event_0.preventDefault();
    event_0.stopPropagation();
    table.setRowSelection({});
    restoreSelectionFocus();
  };
  return <TableLinkComponentContext.Provider value={linkComponent ?? null} data-sentry-element="TableLinkComponentContext.Provider" data-sentry-component="GridRoot" data-sentry-source-file="table.tsx">
      <TableLinkContext.Provider value={linkContext} data-sentry-element="TableLinkContext.Provider" data-sentry-source-file="table.tsx">
        <RowSelectionGesturesContext.Provider value={rowSelectionGestures} data-sentry-element="RowSelectionGesturesContext.Provider" data-sentry-source-file="table.tsx">
          <HeaderDragProvider getViewport={getViewport} isDraggingRef={isHeaderDraggingRef} stickyViewportWidth={isMobile ? viewportWidth : null} data-sentry-element="HeaderDragProvider" data-sentry-source-file="table.tsx">
            <DataGrid
          // The actions column takes the free width, as it does in the `@repo/table` renderer.
          fillColumnId="actions" getCellClassName={cell => cn(getAlignClass(cell.column),
          // The cell holds its own control's hit area, which stretches over the whole cell.
          cell.column.columnDef.meta?.fillsCell && 'relative', cellClassName)}
          // SAFETY: the grid's rows are `data`'s items, so it only ever passes a `TData`.
          getRowProps={getRowProps as DataGridProps<TableFeatures, RowData>['getRowProps']} onColumnSizingPreview={isMobile ? setSizingPreview : undefined} recordCount={data.length} renderCellContent={renderCellContent} renderHeadCellContent={renderHeadCellContent} stickySides={stickySides} table={table} tableClassNames={{
            base: cn(TABLE_DRAGGING_CLASS, className),
            bodyCell: CELL_CLASS,
            bodyRow: cn(ROW_CLASS, isStriped && STRIPED_ROW_CLASS),
            headCell: cn(HEAD_CELL_CLASS, isOneLineHeader && 'py-1.5'),
            headerRow: HEADER_ROW_CLASS,
            headerSticky: 'sticky top-0 z-40',
            resizeHandle: RESIZE_HANDLE_CLASS
          }} tableLayout={{
            columnsPinnable: true,
            columnsResizable: true,
            headerBorder: false,
            headerSticky: true,
            rowBorder: false
          }} data-sentry-element="DataGrid" data-sentry-source-file="table.tsx">
              {bulkActions ? <div className={cn(
            // `isolate` keeps sticky-column z-indices inside the table, below overlays such as panels.
            externalViewport ? 'contents' : 'relative isolate h-full min-h-0 w-full max-w-full min-w-0')} onFocusCapture={handleSelectionFocusCapture} onKeyDown={handleTableKeyDown} ref={containerRef}>
                  {externalViewport ? children : <div className="h-full min-h-0 overflow-auto" data-slot="table-viewport" ref={viewportRef}>
                      {children}
                    </div>}
                  {externalViewport && bulkActionsOverlay ? createPortal(<TableBulkActionBar restoreFocus={restoreSelectionFocus} />, bulkActionsOverlay) : <TableBulkActionBar restoreFocus={restoreSelectionFocus} />}
                </div> : children}
            </DataGrid>
          </HeaderDragProvider>
        </RowSelectionGesturesContext.Provider>
      </TableLinkContext.Provider>
    </TableLinkComponentContext.Provider>;
}
export const TableHeader = ({
  children
}: TableHeaderProps) => {
  // Header groups change with order, pinning and visibility only.
  const {
    table
  } = useTableState(selectLayout);
  const start = table.getStartHeaderGroups();
  const center = table.getCenterHeaderGroups();
  const end = table.getEndHeaderGroups();
  // A column without a group header spans the head rows from its top cell (`rowSpan`), and the
  // cells it covers have a `rowSpan` of 0, as the grid's own head leaves them out.
  const rendered = (headers: Header<RowData, unknown>[] = []) => headers.filter(header => header.rowSpan !== 0);
  return <DataGridTableHead data-sentry-element="DataGridTableHead" data-sentry-component="TableHeader" data-sentry-source-file="table.tsx">
      {center.map((group, rowIndex) => {
      const headerGroup: TableHeaderGroupModel = {
        centerHeaders: rendered(group.headers),
        endHeaders: rendered(end[rowIndex]?.headers),
        id: group.id,
        rowCount: center.length,
        rowIndex,
        startHeaders: rendered(start[rowIndex]?.headers)
      };
      return <Fragment key={group.id}>{children({
          headerGroup
        })}</Fragment>;
    })}
    </DataGridTableHead>;
};
export type TableHeaderGroupProps = {
  children: (props: {
    header: Header<RowData, unknown>;
  }) => ReactNode;
  headerGroup: TableHeaderGroupModel;
};
export const TableHeaderGroup = ({
  children,
  headerGroup
}: TableHeaderGroupProps) => {
  const hasEndPinned = headerGroup.endHeaders.length > 0;
  const fill = headerGroup.rowIndex === 0 ? <DataGridTableFillHeadCell rowSpan={headerGroup.rowCount} /> : null;
  const render = (header: Header<RowData, unknown>) => <Fragment key={header.id}>{children({
      header
    })}</Fragment>;

  // The fill cell takes the free width between the scrolling and the end-pinned columns.
  return <DataGridTableHeadRow rowId={headerGroup.id} data-sentry-element="DataGridTableHeadRow" data-sentry-component="TableHeaderGroup" data-sentry-source-file="table.tsx">
      {[...headerGroup.startHeaders, ...headerGroup.centerHeaders].map(render)}
      {hasEndPinned ? fill : null}
      {headerGroup.endHeaders.map(render)}
      {hasEndPinned ? null : fill}
    </DataGridTableHeadRow>;
};
export type TableHeadProps = {
  header: Header<RowData, unknown>;
};
export const TableHead = ({
  header
}: TableHeadProps) => {
  const {
    props
  } = useDataGrid();
  const {
    column
  } = header;
  const isOneLine = hasOneLineHeader(useRowDensity());
  const registerCell = useHeaderDragCell(column.id);
  const isLeaf = column.columns.length === 0;
  // A column whose pinning is locked keeps its place, so it has no grip. A column outside the
  // column groups heads its column from a placeholder spanning the head rows, which takes it.
  const canDrag = isLeaf && !column.columnDef.meta?.lockedPinning;
  // Through the grid, so a group header's placeholder stays empty.
  const content = renderDataGridTableHeadCellContent(header, props);
  return <DataGridTableHeadRowCell
  // The column keeps its full name under a short label, and the grip's own label stays out of
  // the column's name.
  ariaLabel={canDrag || isOneLine && column.columnDef.meta?.shortLabel ? getColumnHeaderLabel(column) : undefined} dndRef={isLeaf ? registerCell : undefined} header={header} data-sentry-element="DataGridTableHeadRowCell" data-sentry-component="TableHead" data-sentry-source-file="table.tsx">
      {canDrag ? <div className="flex min-w-0 items-center gap-1.5">
          <HeaderDragGrip column={column} />
          {/* The title stays put beside the grip's slot, so a hover never moves it from under the
              pointer. */}
          <div className="min-w-0 flex-1">{content}</div>
        </div> : content}
      {header.column.getCanResize() && <DataGridTableHeadRowCellResize header={header} testId={TEST_IDS.table.resizeHandler(header.id)} />}
    </DataGridTableHeadRowCell>;
};

/** The header content at every head cell the grid renders: our sort menu and controls. */
const renderHeadCellContent = (header: Header<RowData, unknown>) => <TableColumnHeader header={header} data-sentry-element="TableColumnHeader" data-sentry-component="renderHeadCellContent" data-sentry-source-file="table.tsx" />;
const SortIcon = ({
  direction
}: {
  direction: 'asc' | 'desc' | false;
}) => {
  const className = cn(
  // `h-4 w-4`, not `size-4`, so the button's own icon size applies, as in `@repo/table`.
  'ml-2 h-4 w-4 shrink-0 opacity-100 transition-opacity', !direction && 'md:opacity-0 md:group-hover/header:opacity-100');
  switch (direction) {
    case 'asc':
      return <ChevronUpIcon className={className} />;
    case 'desc':
      return <ChevronDownIcon className={className} />;
    default:
      return <ChevronsUpDownIcon className={className} />;
  }
};

// The head cell has no end padding, so the title wraps at the width it does in the `@repo/table`
// renderer and clears the resize handle.
const HEADER_CONTENT_CLASS = 'flex min-h-8 min-w-0 items-center gap-1 pr-1.5';
const TableColumnHeader = ({
  header
}: {
  header: Header<RowData, unknown>;
}) => {
  const {
    column
  } = header;
  const {
    sorting,
    sorts,
    table
  } = useTableState(state => ({
    sorting: state.sorting
  }));
  const isOneLine = hasOneLineHeader(useRowDensity());
  const align = column.columnDef.meta?.align ?? 'start';
  const title = flexRender(column.columnDef.header, header.getContext());
  const shortLabel = isOneLine ? column.columnDef.meta?.shortLabel : undefined;
  // A one-line header truncates the title, or shows a short label with the title on hover.
  const label = isOneLine ? <TruncatedText title={shortLabel ? title : undefined}>{shortLabel ?? title}</TruncatedText> : title;

  // The selection checkbox renders as it is, and an actions column's header control at the end.
  if (isSelectionColumn(column.id)) return title;
  if (column.columnDef.meta?.headerControl) {
    return <div className={cn(HEADER_CONTENT_CLASS, 'justify-end pr-3.5')}>{title}</div>;
  }
  const canSort = column.getCanSort() && sorts.includes(column.id);
  const columnSort = sorting.find(sort => sort.id === column.id);
  const direction = columnSort ? columnSort.desc ? 'desc' : 'asc' : false;
  if (!canSort) {
    return <div className={cn(HEADER_CONTENT_CLASS, align === 'center' && 'justify-center', align === 'end' && 'justify-end')}>
        <span className={cn('w-[calc(100%-5px)] min-w-0 flex-1 py-0.5 text-wrap', align === 'center' && 'text-center', align === 'end' && 'text-right')}>
          {label}
        </span>
      </div>;
  }
  return <div className={HEADER_CONTENT_CLASS} data-sentry-component="TableColumnHeader" data-sentry-source-file="table.tsx">
      <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="table.tsx">
        <DropdownMenuTrigger render={<Button aria-label={shortLabel ? getColumnHeaderLabel(column) : undefined} className={cn('h-auto min-h-8 w-[calc(100%-5px)] min-w-0 justify-between px-0 text-left text-sm data-popup-open:bg-accent', align === 'center' && 'justify-center gap-1 text-center', align === 'end' && 'flex-row-reverse text-right')} size="sm" variant="ghost" />} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="table.tsx">
          <span className={cn('min-w-0 text-wrap', align === 'center' && 'text-center', align === 'end' && 'text-right')}>
            {label}
          </span>
          <SortIcon direction={direction} data-sentry-element="SortIcon" data-sentry-source-file="table.tsx" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" data-sentry-element="DropdownMenuContent" data-sentry-source-file="table.tsx">
          <DropdownMenuItem onClick={() => column.toggleSorting(false)} data-sentry-element="DropdownMenuItem" data-sentry-source-file="table.tsx">
            <ChevronUpIcon className="mr-2 size-3.5 text-muted-foreground/70" data-sentry-element="ChevronUpIcon" data-sentry-source-file="table.tsx" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => column.toggleSorting(true)} data-sentry-element="DropdownMenuItem" data-sentry-source-file="table.tsx">
            <ChevronDownIcon className="mr-2 size-3.5 text-muted-foreground/70" data-sentry-element="ChevronDownIcon" data-sentry-source-file="table.tsx" />
            Desc
          </DropdownMenuItem>
          {sorting.length > 0 && <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => table.resetSorting()}>
                <XIcon className="mr-2 size-3.5 text-muted-foreground/70" />
                Clear sort
              </DropdownMenuItem>
            </>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
};
export type TableBodyProps = {
  children: (props: {
    row: Row<RowData>;
  }) => ReactNode;
  isLoading?: boolean;
  skeletonRowCount?: number;
};
export const TableBody = ({
  children,
  isLoading,
  skeletonRowCount = DEFAULT_PAGE_SIZE
}: TableBodyProps) => {
  const {
    table
  } = useTableState(selectLayout);
  if (isLoading) {
    return <DataGridTableBody>
        <SkeletonRows count={skeletonRowCount} />
      </DataGridTableBody>;
  }
  const rows = table.getRowModel().rows;
  return <DataGridTableBody data-sentry-element="DataGridTableBody" data-sentry-component="TableBody" data-sentry-source-file="table.tsx">
      {rows.length ? rows.map(row => <Fragment key={row.id}>{children({
        row
      })}</Fragment>) : <EmptyRow />}
    </DataGridTableBody>;
};

/** Placeholder rows while rows load. */
const SkeletonRows = ({
  cellClassName,
  count
}: {
  cellClassName?: string;
  count: number;
}) => {
  const {
    table
  } = useTableState(selectLayout);
  const renderSkeleton = (column: Column<RowData, unknown>) => <DataGridTableBodyRowSkeletonCell className={cellClassName} column={column} key={column.id} data-sentry-element="DataGridTableBodyRowSkeletonCell" data-sentry-component="renderSkeleton" data-sentry-source-file="table.tsx">
      <Skeleton className="h-5 rounded-lg" data-sentry-element="Skeleton" data-sentry-source-file="table.tsx" />
    </DataGridTableBodyRowSkeletonCell>;
  return Array.from({
    length: count
  }, (_, rowIndex) => <DataGridTableBodyRowSkeleton key={rowIndex}>
      <FilledRow center={table.getCenterVisibleLeafColumns().map(renderSkeleton)} end={table.getEndVisibleLeafColumns().map(renderSkeleton)} fill={<DataGridTableFillBodyCell />} start={table.getStartVisibleLeafColumns().map(renderSkeleton)} />
    </DataGridTableBodyRowSkeleton>);
};

/** As the `@repo/table` renderer shows no rows, across every column and the fill cell. */
const EmptyRow = () => {
  const {
    table
  } = useTableState(selectLayout);
  return <tr className="bg-background transition-colors hover:bg-muted" data-sentry-component="EmptyRow" data-sentry-source-file="table.tsx">
      <td className="h-24 p-2 text-center align-middle" colSpan={table.getVisibleLeafColumns().length + 1}>
        No results.
      </td>
    </tr>;
};

/** A row's cells in grid order, with the fill cell before any end-pinned cells. */
const FilledRow = ({
  center,
  end,
  fill,
  start
}: {
  center: ReactNode[];
  end: ReactNode[];
  fill: ReactNode;
  start: ReactNode[];
}) => <>
    {start}
    {center}
    {end.length > 0 ? fill : null}
    {end}
    {end.length > 0 ? null : fill}
  </>;
export type TableRowProps = {
  children: (props: {
    cell: Cell<RowData, unknown>;
  }) => ReactNode;
  className?: string;
  /** Paints the selected-row background without checking the row or opening bulk actions. */
  highlighted?: boolean;
  measureElement?: React.RefCallback<HTMLTableRowElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  row: Row<RowData>;
  virtualIndex?: number;
};
export const TableRow = ({
  children,
  className,
  highlighted,
  measureElement,
  onMouseEnter,
  onMouseLeave,
  row,
  virtualIndex
}: TableRowProps) => {
  // A row's cells change with order, pinning and visibility, and its `data-state` with its
  // selection.
  useTableState(state => ({
    ...selectLayout(state),
    rowSelected: Boolean(state.rowSelection[row.id])
  }));
  const render = (cell: Cell<RowData, unknown>) => <Fragment key={cell.id}>{children({
      cell
    })}</Fragment>;
  return <DataGridTableBodyRow className={className} dataIndex={virtualIndex} highlighted={highlighted} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} row={row} rowRef={measureElement} data-sentry-element="DataGridTableBodyRow" data-sentry-component="TableRow" data-sentry-source-file="table.tsx">
      <FilledRow center={row.getCenterVisibleCells().map(render)} end={row.getEndVisibleCells().map(render)} fill={<DataGridTableFillBodyCell />} start={row.getStartVisibleCells().map(render)} data-sentry-element="FilledRow" data-sentry-source-file="table.tsx" />
    </DataGridTableBodyRow>;
};
export type TableCellProps = {
  cell: Cell<RowData, unknown>;
};
export const TableCell = ({
  cell
}: TableCellProps) => <DataGridTableBodyRowCell cell={cell} data-sentry-element="DataGridTableBodyRowCell" data-sentry-component="TableCell" data-sentry-source-file="table.tsx">{renderCellContent(cell)}</DataGridTableBodyRowCell>;

/** Aligns a body or footer cell by `meta.align`, as the headers already are. */
const getAlignClass = (column: Column<RowData, unknown>) => {
  const align = column.columnDef.meta?.align;
  if (align === 'center') return 'text-center';
  if (align === 'end') return 'text-right';
  return undefined;
};

/** The cell content at every body cell the grid renders: the cell, linked when it has a link. */
const renderCellContent = (cell: Cell<RowData, unknown>) => <TableCellContent cell={cell} data-sentry-element="TableCellContent" data-sentry-component="renderCellContent" data-sentry-source-file="table.tsx" />;
const TableCellContent = ({
  cell
}: {
  cell: Cell<RowData, unknown>;
}) => {
  const LinkComponent = useContext(TableLinkComponentContext);
  const context = useContext(TableLinkContext);
  const href = LinkComponent ? cell.column.columnDef.meta?.cellLink?.({
    cell,
    context,
    row: cell.row
  }) : undefined;
  // Re-renders on its own column's width commit, for cells that size their content from the
  // column, and on nothing else in the table state: a context read here re-rendered every cell on
  // any change to it.
  const content = <CellColumnIdContext.Provider value={cell.column.id}>
      <Subscribe selector={state => state.columnSizing[cell.column.id]} source={cell.getContext().table.store}>
        {() => flexRender(cell.column.columnDef.cell, cell.getContext())}
      </Subscribe>
    </CellColumnIdContext.Provider>;
  if (!LinkComponent || !href) return content;
  return <TableCellLink href={href} linkComponent={LinkComponent} data-sentry-element="TableCellLink" data-sentry-component="TableCellContent" data-sentry-source-file="table.tsx">
      {content}
    </TableCellLink>;
};
const TableCellLink = ({
  children,
  href,
  linkComponent: LinkComponent
}: TableLinkProps & {
  linkComponent: TableLinkComponent;
}) => <LinkComponent className="-m-2 block min-w-0 p-2" href={href} data-sentry-element="LinkComponent" data-sentry-component="TableCellLink" data-sentry-source-file="table.tsx">
    <span aria-hidden="true" className="absolute inset-0" data-slot="table-cell-link-area" />
    <div className="relative min-w-0">{children}</div>
  </LinkComponent>;
export type TableFooterProps = {
  /** `isLeading` marks the first visible column other than row selection. */
  children: (props: {
    column: Column<RowData, unknown>;
    isLeading: boolean;
  }) => ReactNode;
};

// A thick top border sets the footer apart as a summary, and it stays at the bottom of the viewport
// while the body scrolls, pinned cells above pinned body cells. The extra bottom padding keeps
// totals clear of overlay scrollbars (macOS).
const FOOTER_SURFACE_CLASS = 'sticky bottom-0 z-40 border-t-[3px] border-b border-border border-t-foreground bg-card data-pinned:z-50! data-pinned:bg-clip-border!';
const FOOTER_CELL_CLASS = cn(FOOTER_SURFACE_CLASS, 'overflow-hidden p-2 pb-5 align-top text-foreground');

/** One row with a cell per visible column, in ReUI's `<tfoot>`. Pinned cells stick with their column. */
export const TableFooter = ({
  children
}: TableFooterProps) => <DataGridTableFoot data-sentry-element="DataGridTableFoot" data-sentry-component="TableFooter" data-sentry-source-file="table.tsx">
    <TableFooterRow data-sentry-element="TableFooterRow" data-sentry-source-file="table.tsx">{children}</TableFooterRow>
  </DataGridTableFoot>;

/** `TableFooter`'s row alone, for a grid that renders the `<tfoot>` itself (`VirtualTable`). */
export const TableFooterRow = ({
  children
}: TableFooterProps) => {
  const {
    table
  } = useTableState(selectLayout);
  const start = table.getStartVisibleLeafColumns();
  const center = table.getCenterVisibleLeafColumns();
  const end = table.getEndVisibleLeafColumns();
  const leadingColumnId = [...start, ...center, ...end].find(column => !isSelectionColumn(column.id))?.id;
  const render = (column_0: Column<RowData, unknown>) => <DataGridTableFootRowCell className={cn(FOOTER_CELL_CLASS, getAlignClass(column_0))} column={column_0} key={column_0.id} testId={TEST_IDS.table.footerCell(column_0.id)} data-sentry-element="DataGridTableFootRowCell" data-sentry-component="render" data-sentry-source-file="table.tsx">
      {/* A total follows its column's Field selection, like the column's cells. */}
      <CellColumnIdContext.Provider value={column_0.id} data-sentry-element="CellColumnIdContext.Provider" data-sentry-source-file="table.tsx">
        {children({
        column: column_0,
        isLeading: column_0.id === leadingColumnId
      })}
      </CellColumnIdContext.Provider>
    </DataGridTableFootRowCell>;
  return <DataGridTableFootRow fillCell={false} testId={TEST_IDS.table.footerRow} data-sentry-element="DataGridTableFootRow" data-sentry-component="TableFooterRow" data-sentry-source-file="table.tsx">
      <FilledRow center={center.map(render)} end={end.map(render)} fill={<DataGridTableFillFootCell className={FOOTER_SURFACE_CLASS} />} start={start.map(render)} data-sentry-element="FilledRow" data-sentry-source-file="table.tsx" />
    </DataGridTableFootRow>;
};
export type VirtualTableProps<TData extends RowData> = Pick<GridRootProps<TData>, 'bulkActionsOverlay' | 'cellClassName' | 'className' | 'data' | 'getRowProps' | 'isStriped' | 'linkComponent' | 'linkContext'> & {
  /** A row's height in pixels until it renders and is measured. */
  estimateSize: number;
  /** A `TableFooterRow`, which stays at the bottom of the viewport. */
  footer?: ReactNode;
  /** Shows skeleton rows in place of the rows, as while a re-sort loads. */
  isLoading?: boolean;
  /** Asks for more rows once the last row renders, again only after the rows change. */
  onEndReached?: () => void;
  /** Classes for the skeleton cells, which keep the grid's own padding whatever `cellClassName`. */
  skeletonCellClassName?: string;
  skeletonRowCount?: number;
  /** The element that scrolls the table both ways, and that the header sticks to. */
  viewportRef: RefObject<HTMLDivElement | null>;
};

/**
 * The table with only its rows in view rendered, on the grid's virtual table: the same header,
 * cells, footer and bulk actions as `TableProvider`, with every row measured as it renders.
 */
export function VirtualTable<TData extends RowData>({
  cellClassName,
  estimateSize,
  footer,
  isLoading,
  onEndReached,
  skeletonCellClassName,
  skeletonRowCount = DEFAULT_PAGE_SIZE,
  viewportRef,
  ...props
}: VirtualTableProps<TData>) {
  const {
    tableRef
  } = useTableState(() => ({}));
  let rows: ReactNode;
  if (isLoading) {
    rows = <SkeletonRows cellClassName={skeletonCellClassName} count={skeletonRowCount} />;
  } else if (props.data.length === 0) {
    rows = <EmptyRow />;
  }
  return <GridRoot {...props} cellClassName={cellClassName} externalViewport viewportRef={viewportRef} data-sentry-element="GridRoot" data-sentry-component="VirtualTable" data-sentry-source-file="table.tsx">
      <DataGridTableVirtual columnVirtualizerOptions={COLUMN_VIRTUALIZER_OPTIONS} estimateSize={estimateSize} footerContent={footer} header={<VirtualTableHeader />}
    // No `hasMore` or `isFetchingMore`: the grid would add its own loading and end rows. It
    // doesn't fetch while `rows` replace the body.
    onFetchMore={onEndReached} overscan={6} rows={rows} scrollElementRef={viewportRef} tableRef={tableRef} virtualizerOptions={VIRTUALIZER_OPTIONS} data-sentry-element="DataGridTableVirtual" data-sentry-source-file="table.tsx" />
    </GridRoot>;
}

// Rows render only the columns in view, beyond the pinned ones: a wide loads view mounts 80-odd
// cells a row otherwise. The header and footer keep every column, so the reveal finds any one.
const COLUMN_VIRTUALIZER_OPTIONS = {
  enabled: true,
  overscan: 4
};

// Rows grow with their content, so each is measured, not taken at the estimate: from the
// ResizeObserver's entry when there is one, as reading the row's box would lay the page out again.
const VIRTUALIZER_OPTIONS = {
  measureElement: (element: HTMLTableRowElement, entry: ResizeObserverEntry | undefined) => entry?.borderBoxSize[0]?.blockSize ?? element.getBoundingClientRect().height
};
const VirtualTableHeader = () => <TableHeader data-sentry-element="TableHeader" data-sentry-component="VirtualTableHeader" data-sentry-source-file="table.tsx">
    {({
    headerGroup
  }) => <TableHeaderGroup headerGroup={headerGroup} key={headerGroup.id}>
        {({
      header
    }) => <TableHead header={header} key={header.id} />}
      </TableHeaderGroup>}
  </TableHeader>;